// Bileli · modules/account/model — the current user's PROFILE repository. why: setting the display name
// via PATCH /me/profile ALSO renames the caller's personal org, which is the exact backend gate for
// publishing a course ("Set your name in your profile…"). Ported from frontend/web modules/users/api.
// NOTE the field asymmetry: the request key is `fullName`, the response reads back as `displayName`.

import { getGlobal, setGlobal } from '../../../../global';

import type { GlobalState } from '../../../../global/types';
import type { BileliUser } from '../../../../util/bileli/types';

import { getMainUsername, getUserFullName } from '../../../../global/helpers';
import { updateBileliAuth } from '../../../../global/reducers/bileli';
import { selectUser } from '../../../../global/selectors';
import { bileliApiJson, parseApi, schemas } from '../../../shared';
import { isUsableProfileName } from './profile-name';

export { isUsableProfileName } from './profile-name';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** PATCH /me/profile — sets the display name AND renames the personal org (unblocks publish). */
export async function updateMyProfile(fullName: string): Promise<BileliUser> {
  return bileliApiJson<BileliUser>('/me/profile', {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ fullName }),
  });
}

/**
 * The signed-in Telegram user's display name (first+last / username), or undefined if absent or unusable.
 * The ONE place the Bileli layer reads Telegram's global — encapsulated so the login auto-fill and the
 * publish self-heal share a single source instead of touching global elsewhere.
 */
export function telegramDisplayName(): string | undefined {
  const global = getGlobal();
  const user = global.currentUserId ? selectUser(global, global.currentUserId) : undefined;
  const name = getUserFullName(user)?.trim();
  return name && isUsableProfileName(name) ? name : undefined;
}

/**
 * The signed-in Telegram user's active @username (no leading @). Takes the CALLER's `global` (unlike
 * telegramDisplayName) so withGlobal mappers stay reactive — the username nudge re-renders the moment the
 * user saves a username in the fork's own settings. Telegram-global reads stay confined to this file.
 */
export function selectTelegramUsername(global: GlobalState): string | undefined {
  const user = global.currentUserId ? selectUser(global, global.currentUserId) : undefined;
  return user ? getMainUsername(user) : undefined;
}

/** Merge a fresh user into the session so the greeting/header update immediately after a name change. */
export function setSessionUser(user: BileliUser): void {
  // tt-multitab safety: setGlobal must receive the `global` variable it was derived from.
  let global = getGlobal();
  global = updateBileliAuth(global, { user });
  setGlobal(global);
}

/**
 * Name the personal org from the Telegram name when it's still unnamed. why: Telegram signup sets the
 * user's `displayName` but NEVER renames the personal org (stays "Personal"), which is the actual publish
 * gate — so we key off the ORG name, not `displayName` (which is never blank for a Telegram user). Only
 * PATCHes when a USABLE Telegram name exists and the personal org isn't already that name; best-effort
 * (any failure returns the user unchanged so sign-in never breaks). Emoji/blank names → skipped here and
 * handled by the manual entry at publish.
 */
export async function ensureProfileNamed(user: BileliUser): Promise<BileliUser> {
  const name = telegramDisplayName();
  if (!name) return user;
  try {
    const orgs = parseApi(schemas.OrgsSchema, await bileliApiJson('/me/orgs'), 'GET /me/orgs');
    const personal = orgs.find((o) => o.kind === 'PERSONAL');
    if (personal && personal.name !== name) {
      const updated = await updateMyProfile(name);
      setSessionUser(updated);
      return updated;
    }
  } catch {
    // best-effort — leave the profile as-is; publish self-heal / manual entry is the backstop
  }
  return user;
}
